// ============================================================
// FILE: src/pages/BlogPost.jsx  →  SINGLE ARTICLE PAGE
// What a visitor sees after clicking an Insights article:
// full article text + an inquiry form at the bottom.
// ============================================================

import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock } from "lucide-react";
import InquiryForm from "../components/InquiryForm";
import { Reveal } from "../components/Reveal";
import { useSEO } from "../hooks/useSEO";
import { api } from "../lib/api";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useSEO(
    post ? `${post.title} — Evolve Trade & Logistics` : "Insights — Evolve Trade & Logistics",
    post ? post.excerpt : "Trade intelligence from Evolve Trade & Logistics."
  );

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    api.post(slug).then(setPost).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="container-x flex min-h-[70vh] flex-col items-center justify-center pt-24 text-center" data-testid="post-not-found">
        <h1 className="font-display text-4xl font-bold">Article not found</h1>
        <Link to="/insights" className="btn-primary mt-8" data-testid="post-not-found-back">Back to Insights</Link>
      </div>
    );
  }

  if (!post) {
    return <div className="container-x pt-40"><div className="mx-auto h-96 max-w-3xl animate-pulse rounded-2xl bg-slate-900/5 dark:bg-white/5" /></div>;
  }

  return (
    <article data-testid="blog-post">
      <section className="relative overflow-hidden pb-12 pt-40">
        <div className="bg-grid pointer-events-none absolute inset-0 opacity-60" />
        <div className="container-x relative max-w-4xl">
          <Link to="/insights" className="inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-teal-500" data-testid="post-back-link">
            <ArrowLeft className="h-4 w-4" /> All Insights
          </Link>
          <Reveal>
            <span className="section-label mt-8 block">{post.category}</span>
            <h1 className="mt-4 font-display text-4xl font-bold leading-[1.08] tracking-tighter sm:text-5xl">{post.title}</h1>
            <p className="mt-5 flex items-center gap-4 text-sm text-muted-foreground">
              <span>{new Date(post.date).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</span>
              <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" /> {post.read_time}</span>
            </p>
          </Reveal>
        </div>
      </section>

      <section className="container-x max-w-5xl">
        <Reveal>
          <div className="overflow-hidden rounded-[2rem] border border-slate-900/10 dark:border-white/10">
            <img src={post.image} alt={post.title} className="aspect-[21/9] w-full object-cover" />
          </div>
        </Reveal>
      </section>

      <section className="container-x max-w-3xl py-16" data-testid="post-content">
        {post.content.map((block, i) =>
          block.type === "h2" ? (
            <Reveal key={i}>
              <h2 className="mb-4 mt-12 font-display text-2xl font-semibold tracking-tight sm:text-3xl">{block.text}</h2>
            </Reveal>
          ) : (
            <Reveal key={i}>
              <p className="mb-6 text-base leading-[1.85] text-muted-foreground">{block.text}</p>
            </Reveal>
          )
        )}
      </section>

      <section className="container-x max-w-4xl pb-24 sm:pb-32">
        <Reveal>
          <div className="relative overflow-hidden rounded-[2rem] border border-teal-500/20 bg-gradient-to-br from-blue-900 via-navy to-slate-950 p-8 sm:p-12">
            <div className="bg-grid pointer-events-none absolute inset-0 opacity-40" />
            <div className="relative">
              <h2 className="font-display text-2xl font-bold text-white sm:text-3xl">Put this insight to work</h2>
              <p className="mt-3 max-w-lg text-sm text-slate-300">
                Our trade desk turns analysis into action — quotations, supplier shortlists and lane design within 24 hours.
              </p>
              <div className="mt-8">
                <InquiryForm type="insights" compact testIdPrefix="post-inquiry" />
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </article>
  );
}
